import { isSetFilterActive } from '@/components/grid/filters/setFilterModel'
import { isStateBucketModel } from './state-bucket-filter'
import { STATION_STATE_BUCKET_IDS } from './station-state-buckets'
import type { GridSetFilterModel, GridSortModelItem } from '@/components/grid/types'

/**
 * Maps the grid's filter model onto the station endpoint's filter parameters.
 *
 * SCRUM-1071 gave `GET /api/station` a repeatable numeric-id parameter per
 * column: values repeat to OR within a column, different parameters AND across
 * columns, and `totalCount` reflects the result. `query-string` stringifies an
 * array as the repeated form the endpoint expects (`AreaIds=1&AreaIds=6`), the
 * same way the controller list already sends `Purpose`.
 *
 * The grid filters on the value a column *displays* — "Backyard", "Loam" — so
 * every column here needs its catalog to turn those labels back into ids.
 *
 * Status is mapped through its three groups rather than its nine states: the
 * groups are defined as sets of the endpoint's own `StateIds`, so the filter
 * needs no name-to-number guessing (see `station-state-buckets.ts`). Note the
 * endpoint also has a singular `State` taking the display string — it is not
 * repeatable, silently keeping one value out of a multi-select, so it is no use
 * here.
 *
 * Two of the filterable columns cannot be mapped and stay client-side:
 *
 * - **ET Zone** — the endpoint has no filter parameter for it at all.
 * - **Description / Decoder Address** — "contains" searches, not value sets.
 *   `keyword` is the closest thing the endpoint offers and it spans both
 *   columns at once, which is not what a per-column filter means.
 *
 * `isClientOnlyFilterActive` reports when one of those is in use, because the
 * client can only filter rows it holds — the caller has to fetch the list whole
 * before a client-side pass can be correct.
 */

/** Columns the endpoint can filter, in grid-field to parameter order. */
const SERVER_FILTER_FIELDS = [
  'area',
  'subArea',
  'plant',
  'soil',
  'emitter',
  'pipe',
  'stationType',
] as const

/** Columns the grid must filter itself. See the note above. */
const CLIENT_ONLY_FILTER_FIELDS = ['etZone']

export type StationServerFilters = {
  AreaIds?: number[]
  SubAreaIds?: number[]
  PlantIds?: number[]
  SoilIds?: number[]
  EmitterIds?: number[]
  PipeIds?: number[]
  StationTypeIds?: number[]
  StateIds?: number[]
}

export type StationFilterCatalogs = {
  areaOptionsData: any[]
  subAreaOptionsData: any[]
  plantOptionsData: any[]
  soilOptionsData: any[]
  emitterOptionsData: any[]
  pipeOptionsData: any[]
}

const selectedValues = (model: unknown): string[] =>
  isSetFilterActive(model)
    ? (model as GridSetFilterModel).values.map((value) => String(value))
    : []

/**
 * Ids of every catalog entry whose label is selected.
 *
 * A label can repeat — two areas can each own a "Zone 1" sub-area — so this
 * collects every id that matches rather than the first. The endpoint ORs the
 * repeated parameter, so all of them are wanted.
 */
const idsForLabels = (
  entries: any[],
  labelOf: (entry: any) => unknown,
  selected: string[],
): number[] => {
  if (selected.length === 0) return []
  const wanted = new Set(selected)
  const ids = entries
    .filter((entry) => wanted.has(String(labelOf(entry) ?? '')))
    .map((entry) => Number(entry?.id))
    .filter((id) => Number.isFinite(id))
  return [...new Set(ids)]
}

/** The sub-area catalog is grouped by parent area name; flatten it first. */
const flattenSubAreas = (grouped: any[]): any[] =>
  grouped.flatMap((group: any) => group?.subAreas ?? [])

export const isClientOnlyFilterActive = (
  filterModel: Record<string, unknown>,
): boolean =>
  Object.entries(filterModel).some(([field, model]) => {
    if (model == null) return false
    // The status filter carries its own model and is mapped to `StateIds`.
    if (isStateBucketModel(model)) return false
    // A text "contains" model is neither a value set nor server-mappable.
    if (!isSetFilterActive(model)) {
      return typeof model === 'object' && Boolean((model as any).contains)
    }
    return CLIENT_ONLY_FILTER_FIELDS.includes(field)
  })

export const buildStationServerFilters = (
  filterModel: Record<string, unknown>,
  catalogs: StationFilterCatalogs,
): StationServerFilters => {
  const filters: StationServerFilters = {}

  // Status is not a value set, so it is mapped ahead of the set-filter columns.
  const stateModel = filterModel.state
  if (isStateBucketModel(stateModel)) {
    filters.StateIds = stateModel.buckets.flatMap(
      (bucket) => STATION_STATE_BUCKET_IDS[bucket],
    )
  }

  for (const field of SERVER_FILTER_FIELDS) {
    const selected = selectedValues(filterModel[field])
    if (selected.length === 0) continue

    switch (field) {
      case 'area':
        filters.AreaIds = idsForLabels(
          catalogs.areaOptionsData,
          (area) => area?.name,
          selected,
        )
        break
      case 'subArea':
        filters.SubAreaIds = idsForLabels(
          flattenSubAreas(catalogs.subAreaOptionsData),
          (sub) => sub?.name,
          selected,
        )
        break
      case 'plant':
        filters.PlantIds = idsForLabels(
          catalogs.plantOptionsData,
          (plant) => plant?.description,
          selected,
        )
        break
      case 'soil':
        filters.SoilIds = idsForLabels(
          catalogs.soilOptionsData,
          (soil) => soil?.description,
          selected,
        )
        break
      case 'emitter':
        filters.EmitterIds = idsForLabels(
          catalogs.emitterOptionsData,
          (emitter) => emitter?.emitter,
          selected,
        )
        break
      case 'pipe':
        filters.PipeIds = idsForLabels(
          catalogs.pipeOptionsData,
          (pipe) => pipe?.description,
          selected,
        )
        break
      case 'stationType':
        // The only column already filtered on its id — the dropdown's values
        // are the type numbers, shown through a formatter.
        filters.StationTypeIds = [
          ...new Set(selected.map(Number).filter(Number.isFinite)),
        ]
        break
    }
  }

  // An id the catalog could not supply must not become an empty parameter: the
  // endpoint ignores a filter it cannot read, so an unmapped selection would
  // quietly widen the list to everything instead of narrowing it. Dropping the
  // key leaves the column to the client-side pass, which still narrows.
  for (const key of Object.keys(filters) as (keyof StationServerFilters)[]) {
    if ((filters[key] ?? []).length === 0) delete filters[key]
  }

  return filters
}

/**
 * True when a selection was made on a server-filterable column but no id came
 * back for it — the catalog had not loaded, or its labels do not match the
 * rows'. The client-side pass still narrows correctly, but only over rows it
 * holds, so the caller has to take the list whole.
 */
export const hasUnmappedServerFilter = (
  filterModel: Record<string, unknown>,
  filters: StationServerFilters,
): boolean => {
  const parameterFor: Record<string, keyof StationServerFilters> = {
    area: 'AreaIds',
    subArea: 'SubAreaIds',
    plant: 'PlantIds',
    soil: 'SoilIds',
    emitter: 'EmitterIds',
    pipe: 'PipeIds',
    stationType: 'StationTypeIds',
  }

  return SERVER_FILTER_FIELDS.some(
    (field) =>
      selectedValues(filterModel[field]).length > 0 &&
      !filters[parameterFor[field]],
  )
}

// --- Sorting ---------------------------------------------------------------

/**
 * Grid columns the endpoint sorts, and the `SortBy` value each one takes.
 *
 * Verified against staging on 2026-08-20 by comparing every candidate against a
 * control: `SortBy` set to a field name that does not exist. A column whose
 * result matches the control is not being sorted — `SortDirection` alone still
 * reverses the endpoint's own order, which is what made some of these look
 * honoured when they were not.
 *
 * The endpoint's name for a column is not always the grid's: the Decoder
 * Address column sorts as `address`. Any `SortBy` the endpoint does not know
 * falls back to Id order with no error, so a wrong name fails silently — check
 * a new entry against the control, in both directions. `decoderAddress` vs
 * `address` re-verified on 2026-10-20.
 *
 * Everything absent here — ET Zone, and every numeric column (default run time,
 * expected flow, precipitation rate, ...) — matched the control and has to be
 * sorted by the client, over the whole list.
 */
const SERVER_SORT_FIELDS: Record<string, string> = {
  description: 'description',
  decoderAddress: 'address',
  area: 'area',
  subArea: 'subArea',
  plant: 'plant',
  soil: 'soil',
  emitter: 'emitter',
  pipe: 'pipe',
  priority: 'priority',
  stationType: 'stationType',
  state: 'state',
}

export type StationServerSort = {
  SortBy: string
  SortDirection: 'asc' | 'desc'
}

/**
 * The endpoint's sort parameters for the grid's sort, or `null` when the sorted
 * column is not one the endpoint knows — in which case the caller has to take
 * the list whole and let the grid sort it.
 *
 * Only the first sort is sent; the endpoint takes a single field, and the grid
 * only ever reports one.
 */
export const buildStationServerSort = (
  sortModel: GridSortModelItem[],
): StationServerSort | null => {
  const [sort] = sortModel
  if (!sort) return null
  const sortBy = SERVER_SORT_FIELDS[sort.field]
  return sortBy ? { SortBy: sortBy, SortDirection: sort.sort } : null
}
